import {
  EventReportRequestParameters,
  ReportBean,
  ItemResponseReportBeanObject as ReportApiResponse,
} from 'common/api/api.ts'
import { convertError } from 'common/util/Util.tsx'

export interface ReportColumn {
  key: string
  title: string
}

export interface ReportData {
  columns: ReportColumn[]
  rows: any[][]
  totalLines: number
  totalValues?: Record<string, string | number>
  warning?: string
}

type RawReportBean = ReportBean & {
  warning?: string
}

export type ReportLoadResult =
  | {
      kind: 'success'
      data: ReportData
      loadTimeMs: number
      rowCount: number
    }
  | {
      kind: 'error'
      loadTimeMs: number
      rowCount: number
      message: string
    }

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

const normalizeData = (bean?: RawReportBean): ReportData => {
  const columns = (bean?.columns ?? []).map((col, idx) => ({
    key: col?.key || `col_${idx}`,
    title: col?.title || col?.key || `Column ${idx + 1}`,
  }))

  return {
    columns,
    rows: bean?.rows ?? [],
    totalLines: bean?.totalLines ?? bean?.rows?.length ?? 0,
    totalValues: bean?.totalValues as unknown as Record<string, string | number> | undefined,
    warning: typeof bean?.warning === 'string' ? bean.warning.trim() : undefined,
  }
}

const parseError = async (
  error: any,
): Promise<{ status?: number; message?: string }> => {
  const status = typeof error?.status === 'number' ? error.status : undefined
  const message = await convertError(error)

  return {
    status,
    message,
  }
}

export const loadReport = async (
  payload: EventReportRequestParameters,
  reportCall: (request: EventReportRequestParameters) => Promise<ReportApiResponse>,
): Promise<ReportLoadResult> => {
  const startedAt = now()

  try {
    const response = await reportCall(payload)
    const data = normalizeData(response?.data)
    const loadTimeMs = Math.round(now() - startedAt)
    return {
      kind: 'success',
      data,
      loadTimeMs,
      rowCount: data.totalLines,
    }
  } catch (error: any) {
    const parsed = await parseError(error)
    const loadTimeMs = Math.round(now() - startedAt)

    return {
      kind: 'error',
      loadTimeMs,
      rowCount: 0,
      message: parsed.message || 'Failed to load report',
    }
  }
}
